import { Cancel as CancelIcon, PictureAsPdfOutlined } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Button,
    Dialog,
    Grid,
    IconButton,
    Skeleton,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { ClipboardList } from "lucide-react";
import React, { useEffect, useState } from "react";
import { fetchActivePatientBeneficationInsuranceSummaryBybvsId, fetchPatientDocumentsByIds, usePatientReducer } from "../../../redux/slice/patient/patientSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import PDFViewer from "../../common/pdf/PDFViewer";
import { useCommonReducer } from "../../../redux/slice/commonSlice";
import { useModalStyles } from "../../common/useModalStyles";

interface PatientBenefitInsuranceSummaryHistoryProps {
    open: boolean;
    onClose: () => void;
    bvsId: number | null;
}

const PatientBenefitInsuranceDetailsPopup: React.FC<PatientBenefitInsuranceSummaryHistoryProps> = ({
    open,
    onClose,
    bvsId,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDark = theme.palette.mode === "dark";
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        bodySx,
        footerSx,
        closeIconSx,
        primaryBtnSx,
        cancelBtnSx,
    } = useModalStyles();
    const dispatch = useAppDispatch();
    const { patientActiveBeneficationInsuranceSummaryData } = usePatientReducer();
    const [showPDF, setShowPDF] = useState<{ isShow: boolean; patientName: string; fileName: string }>({
        isShow: false,
        patientName: "",
        fileName: "",
    });
    const [pdfContent, setPDFContent] = useState<string>("");
    const { documentDataFromCommonApi } = useCommonReducer();

    const hasRecursive =
        patientActiveBeneficationInsuranceSummaryData &&
        patientActiveBeneficationInsuranceSummaryData?.recursive !== null;

    const pdfBtnSx = {
        ...primaryBtnSx,
        py: 0.65,
        px: 1.5,
        fontSize: "0.8rem",
        minWidth: "auto",
        boxShadow: isDark ? "none" : "0 2px 10px rgba(20,184,166,0.25)",
    };

    const sectionCardSx = {
        border: `1px solid ${tokens.border}`,
        borderRadius: "12px",
        backgroundColor: tokens.cardBg,
        overflow: "hidden" as const,
    };

    const labelSx = {
        fontWeight: 600,
        fontSize: "0.8rem",
        color: tokens.textSecondary,
    };

    const valueSx = {
        fontWeight: 700,
        fontSize: "0.9rem",
        color: tokens.textPrimary,
    };

    const warningOrange = isDark ? "#fb923c" : "#ea580c";

    useEffect(() => {
        if (bvsId) {
            dispatch(fetchActivePatientBeneficationInsuranceSummaryBybvsId(bvsId.toString()));
        }
    }, [bvsId]);

    const onElligblityPdfClick = async (docId: any) => {
        const actionResult = await dispatch(fetchPatientDocumentsByIds(docId));
        if (actionResult.payload) {
            setPDFContent(actionResult.payload.content);
            const patientName = `${patientActiveBeneficationInsuranceSummaryData?.patientName} - Eligibility Check Summary`;
            setShowPDF({ isShow: true, patientName, fileName: patientName });
        }
    };

    const onRecursivePdfClick = async (docId: any) => {
        const actionResult = await dispatch(fetchPatientDocumentsByIds(docId));
        if (actionResult.payload) {
            setPDFContent(actionResult.payload.content);
            const patientName = `${patientActiveBeneficationInsuranceSummaryData?.patientName} - Recursive Summary`;
            setShowPDF({ isShow: true, patientName, fileName: patientName });
        }
    };

    const data = patientActiveBeneficationInsuranceSummaryData;

    return (
        <>
            {showPDF.isShow && (
                <PDFViewer
                    isOpen={showPDF.isShow}
                    onClose={() => {
                        setShowPDF({ isShow: false, patientName: "", fileName: "" });
                    }}
                    title={showPDF.patientName}
                    fileName={showPDF.fileName}
                    base64String={documentDataFromCommonApi?.content ? documentDataFromCommonApi?.content : pdfContent}
                />
            )}
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={hasRecursive ? "lg" : "sm"}
                fullWidth
                fullScreen={isMobile}
                PaperProps={{ sx: paperSx(isMobile) }}
                aria-labelledby="patient-benefit-insurance-dialog-title"
                onClick={(event) => {
                    event.stopPropagation();
                }}
            >
                <Box sx={headerSx}>
                    <Box sx={iconBoxSx(tokens.accent)}>
                        <ClipboardList size={22} color={tokens.accent} strokeWidth={1.8} />
                    </Box>
                    <Box sx={{ minWidth: 0, pr: 4 }}>
                        <Typography
                            id="patient-benefit-insurance-dialog-title"
                            fontWeight={700}
                            sx={{
                                fontSize: "1.1rem",
                                color: tokens.textPrimary,
                                lineHeight: 1.3,
                            }}
                        >
                            Patient Insurance Eligibility Details
                        </Typography>
                        <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                            Eligibility and advantage policy summary
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
                        <CloseIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                </Box>

                <Box sx={bodySx}>
                    {!data ? (
                        <Box sx={{ py: 2 }}>
                            <Skeleton variant="rounded" height={120} sx={{ mb: 2, borderRadius: "12px" }} />
                            <Skeleton variant="rounded" height={200} sx={{ borderRadius: "12px" }} />
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={hasRecursive ? 6 : 12}>
                                <Box sx={sectionCardSx}>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        flexWrap="wrap"
                                        gap={1}
                                        sx={{ px: 2, py: 1.75, borderBottom: `1px solid ${tokens.border}` }}
                                    >
                                        <Typography fontWeight={700} sx={{ color: tokens.accent, fontSize: "1.05rem" }}>
                                            Eligibility Details
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<PictureAsPdfOutlined sx={{ fontSize: "1rem !important" }} />}
                                            onClick={() => {
                                                onElligblityPdfClick(data?.eligibilitySummary?.pdfDocId);
                                            }}
                                            sx={pdfBtnSx}
                                        >
                                            PDF
                                        </Button>
                                    </Box>
                                    <Grid container p={2}>
                                        <Grid item xs={12}>
                                            <Grid container spacing={2}>
                                                <Grid item md={6} xs={6}>
                                                    <Typography sx={labelSx}>Patient:</Typography>
                                                </Grid>
                                                <Grid item md={6} xs={6}>
                                                    <Typography sx={valueSx}>{data?.patientName || "-"}</Typography>
                                                </Grid>

                                                <Grid item md={6} xs={6}>
                                                    <Typography sx={labelSx}>Insurance & Policy #:</Typography>
                                                </Grid>
                                                <Grid item md={6} xs={6}>
                                                    <Typography sx={valueSx}>
                                                        {data?.eligibilitySummary?.insuranceCompanyName &&
                                                        data?.eligibilitySummary?.policy
                                                            ? `${data.eligibilitySummary.insuranceCompanyName} - ${data.eligibilitySummary.policy}`
                                                            : "-"}
                                                    </Typography>
                                                </Grid>

                                                <Grid item md={6} xs={6}>
                                                    <Typography sx={labelSx}>Status:</Typography>
                                                </Grid>
                                                <Grid item md={6} xs={6}>
                                                    <Typography
                                                        sx={{
                                                            ...valueSx,
                                                            color:
                                                                data?.eligibilitySummary?.status === "Inactive"
                                                                    ? tokens.error
                                                                    : "#22c55e",
                                                        }}
                                                    >
                                                        {data?.eligibilitySummary?.status || "-"}
                                                    </Typography>
                                                </Grid>

                                                {data?.eligibilitySummary?.effectiveDate !== null && (
                                                    <>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={labelSx}>Effective Date:</Typography>
                                                        </Grid>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={valueSx}>
                                                                {data?.eligibilitySummary?.effectiveDate || "-"}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}

                                                <Grid item md={6} xs={6}>
                                                    <Typography sx={labelSx}>Policy Type:</Typography>
                                                </Grid>
                                                <Grid item md={6} xs={6}>
                                                    <Typography sx={valueSx}>
                                                        {data?.eligibilitySummary?.policyType || "-"}
                                                        <br />
                                                        <Box
                                                            component="span"
                                                            sx={{ color: warningOrange, fontSize: "0.95rem", fontWeight: 800 }}
                                                        >
                                                            {data?.eligibilitySummary?.isHMOPlan ? "(★ Patient has HMO ★)" : ""}
                                                        </Box>
                                                    </Typography>
                                                </Grid>

                                                {data?.eligibilitySummary?.coInsurance != null && (
                                                    <>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={labelSx}>Co-Insurance:</Typography>
                                                        </Grid>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={valueSx}>
                                                                {data.eligibilitySummary.coInsurance}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}

                                                {data?.eligibilitySummary?.partBDeductible != null && (
                                                    <>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={labelSx}>Part B Deductible:</Typography>
                                                        </Grid>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={valueSx}>
                                                                {data.eligibilitySummary.partBDeductible}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}

                                                {data?.eligibilitySummary?.partBDeductibleRemaining != null && (
                                                    <>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={labelSx}>Part B Deductible Remaining:</Typography>
                                                        </Grid>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={valueSx}>
                                                                {data.eligibilitySummary.partBDeductibleRemaining}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}

                                                {data?.eligibilitySummary?.individualDeductibleInNet != null && (
                                                    <>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={labelSx}>Individual Deductible In Net:</Typography>
                                                        </Grid>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={valueSx}>
                                                                {data.eligibilitySummary.individualDeductibleInNet}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}

                                                {data?.eligibilitySummary?.individualDeductibleRemainingInNet != null && (
                                                    <>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={labelSx}>
                                                                Individual Deductible Remaining In Net:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={valueSx}>
                                                                {data.eligibilitySummary.individualDeductibleRemainingInNet}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}

                                                {data?.eligibilitySummary?.familyDeductibleInNet != null && (
                                                    <>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={labelSx}>Family Deductible In Net:</Typography>
                                                        </Grid>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={valueSx}>
                                                                {data.eligibilitySummary.familyDeductibleInNet}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}

                                                {data?.eligibilitySummary?.familyDeductibleRemainingInNet != null && (
                                                    <>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={labelSx}>
                                                                Family Deductible Remaining In Net:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={valueSx}>
                                                                {data.eligibilitySummary.familyDeductibleRemainingInNet}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}

                                                {data?.eligibilitySummary?.individualOOP_InNet != null && (
                                                    <>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={labelSx}>Individual OOP In Net:</Typography>
                                                        </Grid>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={valueSx}>
                                                                {data.eligibilitySummary.individualOOP_InNet}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}

                                                {data?.eligibilitySummary?.individualOOPRemainingInNet != null && (
                                                    <>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={labelSx}>
                                                                Individual OOP Remaining In Net:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={valueSx}>
                                                                {data.eligibilitySummary.individualOOPRemainingInNet}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}

                                                {data?.eligibilitySummary?.familyOOPInNet != null && (
                                                    <>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={labelSx}>Family OOP In Net:</Typography>
                                                        </Grid>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={valueSx}>
                                                                {data.eligibilitySummary.familyOOPInNet}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}

                                                {data?.eligibilitySummary?.familyOOPRemainingInNet != null && (
                                                    <>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={labelSx}>
                                                                Family OOP Remaining In Net:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item md={6} xs={6}>
                                                            <Typography sx={valueSx}>
                                                                {data.eligibilitySummary.familyOOPRemainingInNet}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {hasRecursive && data?.recursive !== null && (
                                <Grid item xs={12} md={6}>
                                    <Box sx={sectionCardSx}>
                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            flexWrap="wrap"
                                            gap={1}
                                            sx={{ px: 2, py: 1.75, borderBottom: `1px solid ${tokens.border}` }}
                                        >
                                            <Typography fontWeight={700} sx={{ color: tokens.accent, fontSize: "1.05rem" }}>
                                                Advantage Policy
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<PictureAsPdfOutlined sx={{ fontSize: "1rem !important" }} />}
                                                onClick={() => {
                                                    onRecursivePdfClick(data?.recursive?.pdfDocId);
                                                }}
                                                sx={pdfBtnSx}
                                            >
                                                PDF
                                            </Button>
                                        </Box>
                                        <Grid container p={2}>
                                            <Grid item xs={12}>
                                                <Grid container spacing={2}>
                                                    <Grid item md={6} xs={6}>
                                                        <Typography sx={labelSx}>Insurance & Policy #:</Typography>
                                                    </Grid>
                                                    <Grid item md={6} xs={6}>
                                                        <Typography sx={valueSx}>
                                                            {`${data?.recursive?.insuranceCompanyName} - ${data?.recursive?.policy} `}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item md={6} xs={6}>
                                                        <Typography sx={labelSx}>Status:</Typography>
                                                    </Grid>
                                                    <Grid item md={6} xs={6}>
                                                        <Typography
                                                            sx={{
                                                                ...valueSx,
                                                                color:
                                                                    data?.recursive?.status === "Inactive"
                                                                        ? tokens.error
                                                                        : "#22c55e",
                                                            }}
                                                        >
                                                            {data?.recursive?.status || "-"}
                                                        </Typography>
                                                    </Grid>

                                                    {data?.recursive?.effectiveDate !== null && (
                                                        <>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={labelSx}>Effective Date:</Typography>
                                                            </Grid>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={valueSx}>
                                                                    {data?.recursive?.effectiveDate || "-"}
                                                                    <br />
                                                                    <Box
                                                                        component="span"
                                                                        sx={{
                                                                            color: warningOrange,
                                                                            fontSize: "0.95rem",
                                                                            fontWeight: 800,
                                                                        }}
                                                                    >
                                                                        {data?.recursive?.effectiveDate &&
                                                                        new Date(data.recursive.effectiveDate) > new Date()
                                                                            ? "(*Effective Date is in future)"
                                                                            : ""}
                                                                    </Box>
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}

                                                    <Grid item md={6} xs={6}>
                                                        <Typography sx={labelSx}>Policy Type:</Typography>
                                                    </Grid>
                                                    <Grid item md={6} xs={6}>
                                                        <Typography sx={valueSx}>
                                                            {data?.recursive?.policyType || "-"}
                                                            <br />
                                                            <Box
                                                                component="span"
                                                                sx={{ color: warningOrange, fontSize: "0.95rem", fontWeight: 800 }}
                                                            >
                                                                {data?.recursive?.isHMOPlan ? "(★ Patient has HMO ★)" : ""}
                                                            </Box>
                                                        </Typography>
                                                    </Grid>

                                                    {data?.recursive?.coInsurance && (
                                                        <>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={labelSx}>Co-Insurance:</Typography>
                                                            </Grid>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={valueSx}>
                                                                    {data?.recursive?.coInsurance || "-"}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {data?.recursive?.individualDeductibleInNet && (
                                                        <>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={labelSx}>Individual Deductible In Net:</Typography>
                                                            </Grid>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={valueSx}>
                                                                    {data?.recursive?.individualDeductibleInNet || "-"}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {data?.recursive?.individualDeductibleRemainingInNet && (
                                                        <>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={labelSx}>
                                                                    Individual Deductible Remaining In Net:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={valueSx}>
                                                                    {data?.recursive?.individualDeductibleRemainingInNet || "-"}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {data?.recursive?.familyDeductibleInNet && (
                                                        <>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={labelSx}>Family Deductible In Net:</Typography>
                                                            </Grid>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={valueSx}>
                                                                    {data?.recursive?.familyDeductibleInNet || "-"}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {data?.recursive?.familyDeductibleRemainingInNet && (
                                                        <>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={labelSx}>
                                                                    Family Deductible Remaining In Net:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={valueSx}>
                                                                    {data?.recursive?.familyDeductibleRemainingInNet || "-"}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {data?.recursive?.individualOOP_InNet && (
                                                        <>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={labelSx}>Individual OOP In Net:</Typography>
                                                            </Grid>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={valueSx}>
                                                                    {data?.recursive?.individualOOP_InNet || "-"}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {data?.recursive?.individualOOPRemainingInNet && (
                                                        <>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={labelSx}>
                                                                    Individual OOP Remaining In Net:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={valueSx}>
                                                                    {data?.recursive?.individualOOPRemainingInNet || "-"}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {data?.recursive?.familyOOPInNet && (
                                                        <>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={labelSx}>Family OOP In Net:</Typography>
                                                            </Grid>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={valueSx}>
                                                                    {data?.recursive?.familyOOPInNet || "-"}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {data?.recursive?.familyOOPRemainingInNet && (
                                                        <>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={labelSx}>
                                                                    Family OOP Remaining In Net:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item md={6} xs={6}>
                                                                <Typography sx={valueSx}>
                                                                    {data?.recursive?.familyOOPRemainingInNet || "-"}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Box>

                <Box sx={footerSx}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={onClose}
                        startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
                        sx={cancelBtnSx}
                    >
                        Cancel
                    </Button>
                </Box>
            </Dialog>
        </>
    );
};

export default PatientBenefitInsuranceDetailsPopup;
